/**
 * REWE Digital GmbH.
 * IT-Verticals
 *
 * zr-product-tile component
 */

@use "../zr-theme/zr-variables.scss" as *;
@use '../zr-theme/functions/fonts' as f;

// component name
$component: 'zr-product-tile';

// component vars defaults
:host {
    display: block;
    box-sizing: border-box;
    height: 100%;
}

article.#{$component}__sponsored {
    filter: var(--zr-product-tile-sponsored-blur, blur(3px));
}

.#{$component} {
    position: relative;
    display: flex;
    gap: 12px;
    padding: 16px 8px;
    background-color: var(--ci-neutral100);
    border: solid 1px var(--ci-neutral200);
    border-left: none;
    border-right: none;
    cursor: pointer;
    transition: box-shadow .2s ease;

    &:hover {
        box-shadow: 0 5px 15px var(--ci-neutral400);
        z-index: 1;
    }
}

.#{$component}__detail {
    flex: 1 0 55%;
}

.#{$component}__image {
    display: flex;
    justify-content: center;
    position: relative;
    flex: 0 1 auto;
}

.#{$component}__supplier {
    font-size: f.calc-em(12);
    color: var(--ci-neutral800);
    font-weight: 400;
    padding-bottom: 1px;
}

.#{$component}__name {
    font-family: 'Museo Sans Rounded', sans-serif;
    font-size: f.calc-em(16);
    font-weight: 600;
    color: var(--ci-neutral800);
    margin: 0 0 8px;
    line-height: 1.3;
    transition: color .2s ease;

    // "If the product-name is too long" -- Hussein 2. Okt 23
    height: 40px;
    overflow: hidden;
    display: -webkit-box;
    text-overflow: ellipsis;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}

.#{$component}__current-variant {
    font-size: f.calc-em(12);
    color: var(--ci-neutral800);
    padding-bottom: 9px;
}

.#{$component}__variants {
    color: var(--ci-neutral700);
    padding-left: 5px;
}

.#{$component}__add-to-cart {
    clear: both;
    cursor: default;
    position: relative;
    margin: -17px -17px -17px -8px;
    top: -1px;
    text-align: right;
    padding: 8px 17px 16px 8px;

    &--use-points{
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-top: 15px;
    }
}


a {
    color: var(--ci-neutral800);
    text-decoration: none;
    transition: color .2s ease;
    outline-color: var(--ci-primary600);

    :hover &,
    &:hover,
    &:focus {
        .#{$component}__name,
        .#{$component}__variants {
            color: var(--ci-primary400);
        }
    }
}

a:not(.#{$component}__rating):not(.#{$component}__price) {
    outline: none;
}

a.#{$component}__price {
    float: right;
    display: inline-block;
    margin-right: -10px;
    padding-right: 10px;
    padding-left: 10px;
}

// media query: tablet and above
@media only screen and (min-width: $tablet-screen) {
    .#{$component} {
        flex-direction: column;
        margin: 0;
        padding: 8px 16px;
        border: solid 1px var(--ci-neutral200);
        border-radius: 8px;
        height: 100%;
        box-sizing: border-box;

        &__focused {
            border: 2px solid var(--ci-primary600);
            padding: 7px 15px;
        }
    }

    .#{$component}__detail {
        flex: 1 0;
        width: 100%;
    }

    .#{$component}__current-variant {
        font-size: f.calc-em(16);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .#{$component}__variants {
        display: none;

        &--desktop {
            display: block;
            text-align: center;
            font-weight: 300;
        }
    }

    .#{$component}__add-to-cart {
        top: -7px;
    }

    .#{$component}__add-to-cart--use-points {
        flex-direction: row;
        justify-content: space-between;
        gap: 0;
    }
}

zr-rating-badge {
    float: left;
    display: inline-block;

    --rating-star-size: 18px;
    --rating-text-size: 12px;

    @media only screen and (min-width: $tablet-screen) {
        --rating-text-size: 14px;
    }
}

zr-price {
    min-height: 54px;
    text-align: right;
    margin-top: 6px;
    margin-bottom: 14px;

    --zr-current-price-font-size: 18px;
    --zr-current-price-line-height: 18px;
    --zr-recommended-price-font-size: 12px;
    --zr-recommended-price-line-height: 12px;
    --zr-current-price-per-unit-font-size: 12px;
    --zr-current-price-per-unit-line-height: 12px;

    @media only screen and (min-width: $tablet-screen) {
        --zr-current-price-font-size: 20px;
        --zr-current-price-line-height: 20px;
        --zr-recommended-price-font-size: 14px;
        --zr-recommended-price-line-height: 14px;
        --zr-current-price-per-unit-font-size: 14px;
        --zr-current-price-per-unit-line-height: 14px;
    }
}

vx-picture {
    @media only screen and (min-width: $desktop-screen) {
        height: 236px;
    }
}

vx-picture::part(image) {
    display: block;

    @media only screen and (min-width: $tablet-screen) {
        max-height: 189px;
    }

    @media only screen and (min-width: $desktop-screen) {
        max-height: none;
    }
}
